import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Sun, Wind, Moon, Flame, Shield, User, Crown, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import CountdownTimer from "@/components/CountdownTimer";

import { API } from "@/lib/api";

const CLARITY_DEEP = [
  {
    letter: "C", title: "Clean Food & Nutrition", icon: Crown,
    detail: "Strip back to whole, unprocessed food. Cut the sugar, cut the alcohol, cut the excuses. Your gut is your second brain \u2014 when you clean it, your mind follows. Chris spent forty years poisoning his body. The turnaround started at the plate.",
  },
  {
    letter: "L", title: "Light & Sunshine", icon: Sun,
    detail: "Get 20 minutes of direct morning sunlight. It resets your circadian rhythm, boosts serotonin, and tells your nervous system it's safe. No sunglasses. No screens. Just you and the sky. This is the simplest prescription on the list and possibly the most powerful.",
  },
  {
    letter: "A", title: "Air & Breathwork", icon: Wind,
    detail: "Conscious breathing \u2014 box breathing, Wim Hof, or simple 4-7-8 cycles \u2014 activates the parasympathetic nervous system. When Chris stopped white-knuckling his way through anxiety and started breathing through it, everything shifted.",
  },
  {
    letter: "R", title: "Rest & Resistance Training", icon: Moon,
    detail: "Sleep is not optional. Eight hours. Consistent schedule. Dark room. And resistance training isn't about aesthetics \u2014 it's medicine. Lifting heavy things rewires your stress response and builds the discipline that carries over into every area of life.",
  },
  {
    letter: "I", title: "Inflammation & Insulin Resistance", icon: Flame,
    detail: "Chronic inflammation is the silent killer behind depression, brain fog, and metabolic collapse. Stabilise your blood sugar. Remove inflammatory foods. The fog lifts. The energy returns. Your body starts working with you instead of against you.",
  },
  {
    letter: "T", title: "Temperance & Trust", icon: Shield,
    detail: "Moderation in all things \u2014 even recovery. Don't white-knuckle it. Don't swing to the opposite extreme. Trust the process. Trust yourself. Trust that the small, daily actions compound into something extraordinary. Chris tried seven rehabs before he learned this.",
  },
  {
    letter: "Y", title: "You \u2014 It's All On You", icon: User,
    detail: "Nobody is coming to save you. No guru. No pill. No shortcut. The Clarity Protocol works because you decide to work it. Ownership is the final pillar because without it, the other six don't matter. This is where freedom lives.",
  },
];

export default function ClarityProtocolPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGuideDownload = async () => {
    if (!email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { email, lead_type: "guide" });
      navigate("/thank-you?type=guide");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="clarity-protocol-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="font-brand text-2xl tracking-tighter text-white">FYB</Link>
          <Link to="/" className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors flex items-center gap-1">
            <ArrowRight className="w-3 h-3 rotate-180" /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 text-center">
        <div className="max-w-3xl mx-auto space-y-6">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold animate-fade-in">The Clarity Protocol</p>
          <h1 className="font-heading text-5xl sm:text-7xl tracking-tighter uppercase text-white opacity-0 animate-fade-in-up stagger-1">
            Reset Your Nervous System In 30 Days
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto opacity-0 animate-fade-in-up stagger-2">
            After forty years of abusing every system in his body, Chris Merlo developed a simple,
            seven-pillar framework that brought him back from the edge. No gurus. No retreats. No miracles.
            Just seven letters that changed everything.
          </p>
          <div className="gold-line max-w-xs mx-auto" />
        </div>
      </section>

      {/* CLARITY Breakdown */}
      <section className="pb-24 px-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {CLARITY_DEEP.map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={item.letter}
                className="border-gold-glow bg-[#121212] p-8 sm:p-10 rounded-sm"
                data-testid={`clarity-detail-${item.letter}`}
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
                      <span className="font-brand text-3xl text-[#D4AF37]">{item.letter}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-[#D4AF37]" />
                      <h2 className="font-heading text-2xl sm:text-3xl tracking-tight uppercase text-white">{item.title}</h2>
                    </div>
                    <p className="text-zinc-300 leading-relaxed">{item.detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-[#0a0a0a] border-t border-white/10">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white">
            Ready To Start?
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto leading-relaxed">
            Download the free Clarity Mini-Guide and start applying the protocol today.
            Or pre-order the full book for the complete story behind each pillar.
          </p>

          {/* Mini-Guide CTA */}
          <div className="bg-[#121212] border border-white/10 p-8 rounded-sm max-w-md mx-auto space-y-4">
            <h3 className="font-heading text-xl tracking-tight uppercase text-white">Get The Free Mini-Guide</h3>
            <Input
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="protocol-page-email-input"
            />
            <Button
              onClick={handleGuideDownload}
              disabled={loading}
              className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
              data-testid="protocol-page-guide-button"
            >
              {loading ? "Sending..." : "Download Free Mini-Guide"}
            </Button>
          </div>

          {/* Pre-order CTA */}
          <div className="space-y-4">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">Or Get The Full Book</p>
            <div className="flex justify-center">
              <CountdownTimer />
            </div>
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 btn-gold px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm"
              data-testid="protocol-page-preorder-button"
            >
              Pre-Order &mdash; $29.99 <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-zinc-600 text-xs">
              <span className="text-zinc-500 text-xs">$29.99 AUD + shipping</span>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
